import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { isPasswordValid, MIN_PASSWORD_LENGTH } from '@/utils/passwordValidation.js';

const SecuritySettingsPage = () => {
  const { t } = useTranslation('dashboard');
  const { user, initializing, changePassword } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (form.newPassword !== form.confirmPassword) {
      setError(t('security.messages.mismatch'));
      return;
    }

    if (!isPasswordValid(form.newPassword)) {
      setError(t('security.messages.length', { length: MIN_PASSWORD_LENGTH }));
      return;
    }

    try {
      setPending(true);
      const response = await changePassword(form.currentPassword, form.newPassword);
      setSuccess(response?.message || t('security.messages.saved'));
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setPending(false);
    }
  };

  if (initializing && !user) {
    return (
      <div className="page-card">
        <p className="page-description">{t('security.messages.loading')}</p>
      </div>
    );
  }

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <p className="page-caption">{t('security.heading.caption')}</p>
          <h1>{t('security.heading.title')}</h1>
          <p className="page-description">{t('security.heading.description')}</p>
        </div>
      </div>

      <form className="game-form" onSubmit={handleSubmit}>
        <section className="profile-section form-grid-limited">
          <h2>{t('security.form.title')}</h2>
          <p className="profile-hint">{t('security.form.hint', { length: MIN_PASSWORD_LENGTH })}</p>
          <div className="form-grid">
            <label className="form-field">
              <span>{t('security.form.current')}</span>
              <input
                type="password"
                name="currentPassword"
                value={form.currentPassword}
                onChange={handleChange}
                autoComplete="current-password"
                required
              />
            </label>
            <label className="form-field">
              <span>{t('security.form.new')}</span>
              <input
                type="password"
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                autoComplete="new-password"
                required
              />
            </label>
            <label className="form-field">
              <span>{t('security.form.confirm')}</span>
              <input
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                required
              />
            </label>
          </div>
        </section>

        {error && <p className="form-error">{error}</p>}
        {success && <p className="form-success">{success}</p>}
        <button className="primary-button" type="submit" disabled={pending}>
          {pending ? t('security.messages.saving') : t('security.messages.save')}
        </button>
      </form>
    </div>
  );
};

export default SecuritySettingsPage;
